// src/ai/CharacterPromptManager.js
export default class CharacterPromptManager {
  constructor({ hostId = "OLLIE", basePath = "/characters" } = {}) {
    this.hostId = hostId;
    this.basePath = basePath;
    this.data = {};
    this.cursors = new Map();
    this.usedOnceIds = new Set();
    this.loaded = false;
  }

  async load() {
    const urls = [
      `${this.basePath}/${this.hostId}.json`,
      `${this.basePath}/${this.hostId}.txt`
    ];
    for (const url of urls) {
      try {
        const resp = await fetch(url, { cache: "no-cache" });
        if (!resp.ok) continue;
        const ctype = (resp.headers.get("content-type") || "").toLowerCase();
        let parsed = {};
        if (ctype.includes("application/json") || url.endsWith(".json")) {
          parsed = await resp.json();
        } else {
          const text = await resp.text();
          parsed = this.parseIniLike(text);
        }
        this.data = this.normalize(parsed);
        this.loaded = true;
        return true;
      } catch {}
    }
    return false;
  }

  parseIniLike(text) {
    const out = {};
    let section = null;
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line || line.startsWith("#") || line.startsWith("//")) continue;
      const m = line.match(/^\[([^\]]+)\]\s*$/);
      if (m) { section = m[1].trim(); if (!out[section]) out[section] = []; continue; }
      if (!section) continue;
      const kv = line.split(/\s+/);
      let item = null;
      for (const token of kv) {
        if (token.startsWith("tts:"))   item = { kind: "tts",   text: token.slice(4) };
        else if (token.startsWith("video:")) item = { kind: "video", src: token.slice(6) };
        else if (token.startsWith("id=")) { if (!item) item = {}; item.id = token.slice(3); }
      }
      if (item && (item.text || item.src)) out[section].push(item);
    }
    return out;
  }

  normalize(obj) {
    const norm = {};
    for (const [event, arr] of Object.entries(obj || {})) {
      if (event === "_meta") continue;
      const items = Array.isArray(arr) ? arr : [];
      norm[event] = items
        .map(x => ({
          kind: (x.kind || (x.src ? "video" : "tts")).toLowerCase(),
          src: x.src || null,
          text: x.text || null,
          id: x.id || (x.src || x.text || "").slice(0, 80),
          // Facts that must be true for the line to make sense, e.g. "speedBonus".
          needs: Array.isArray(x.needs) ? x.needs : (typeof x.needs === "string" && x.needs ? [x.needs] : [])
        }))
        .filter(it => (it.kind === "video" && it.src) || (it.kind === "tts" && it.text));
    }
    return norm;
  }

  resetQuiz() {
    this.cursors.clear();
    this.usedOnceIds.clear();
  }

  /**
   * A random video for the event that hasn't played this quiz, or null. Each plays at most once
   * per quiz, and across quizzes on this device none repeats until all of the event's videos have
   * played (the history is kept in localStorage), so regular players don't always see the same one.
   */
  nextVideo(event) {
    const videos = (this.data[event] || []).filter(x => x.kind === "video" && !this.usedOnceIds.has(x.id));
    if (!videos.length) return null;
    const history = CharacterPromptManager.readVideoHistory();
    const allIds = (this.data[event] || []).filter(x => x.kind === "video").map(x => x.id);
    let seen = (history[event] || []).filter(id => allIds.includes(id));
    let fresh = videos.filter(x => !seen.includes(x.id));
    if (!fresh.length) {
      // Start the cycle over, but not with the clip that played last.
      const last = seen[seen.length - 1];
      seen = [];
      fresh = videos.length > 1 ? videos.filter(x => x.id !== last) : videos;
    }
    const v = fresh[Math.floor(Math.random() * fresh.length)];
    this.usedOnceIds.add(v.id);
    history[event] = [...seen, v.id];
    CharacterPromptManager.writeVideoHistory(history);
    return v;
  }

  static readVideoHistory() {
    try {
      const h = JSON.parse(localStorage.getItem("ollie_video_history") || "{}");
      return h && typeof h === "object" ? h : {};
    } catch {
      return {};
    }
  }

  static writeVideoHistory(history) {
    try { localStorage.setItem("ollie_video_history", JSON.stringify(history)); } catch {}
  }

  /** `{PLACEHOLDER}` names used in a line. */
  static placeholders(text) {
    return String(text || "").match(/\{[A-Z_]+\}/g) || [];
  }

  static fill(text, context = {}) {
    let t = String(text || "");
    for (const [k, v] of Object.entries(context || {})) t = t.split(k).join(String(v));
    return t;
  }

  /**
   * Next cue for an event: an unused video first (when `allowVideo`, see nextVideo), otherwise a spoken line
   * picked at random from those whose {PLACEHOLDERS} can be filled from `context` and whose
   * `needs` are all in `facts`, never repeating a line until every usable one has been said.
   * With `repeats` false it returns null rather than start over. TTS cues come back filled.
   */
  next(event, { allowVideo = true, context = {}, facts = [], repeats = true } = {}) {
    const list = this.data[event] || [];
    if (!list.length) return null;

    if (allowVideo) {
      const video = this.nextVideo(event);
      if (video) return video;
    }

    // A line with a blank the host can't fill, or a claim that isn't true right now, is skipped.
    const factSet = new Set(facts || []);
    const ctx = {};
    for (const [k, v] of Object.entries(context || {})) {
      if (v !== undefined && v !== null && String(v) !== "") ctx[k] = v;
    }
    const pool = list.filter(x =>
      x.kind === "tts" &&
      (x.needs || []).every(n => factSet.has(n)) &&
      CharacterPromptManager.placeholders(x.text).every(ph => ph in ctx)
    );
    if (!pool.length) return null;

    let used = this.cursors.get(event);
    if (!(used instanceof Set)) used = new Set();
    let fresh = pool.filter(x => !used.has(x.id));
    if (!fresh.length) {
      if (!repeats) return null;
      used = new Set();
      fresh = pool;
    }
    const pick = fresh[Math.floor(Math.random() * fresh.length)];
    used.add(pick.id);
    this.cursors.set(event, used);
    return { ...pick, text: CharacterPromptManager.fill(pick.text, ctx) };
  }

  static format(text, { qNumber = 0, qType = "", qText = "" } = {}) {
    return String(text || "")
      .replace(/\{QNUM\}/g, String(qNumber))
      .replace(/\{QTYPE\}/g, String(qType))
      .replace(/\{QTEXT\}/g, String(qText));
  }
}
